/**
 * Jurisdictions Admin Service — SystemAdmin only.
 * v1 surfaces the row in read-only form plus a single editable knob: DataRetentionDays.
 * Browser-automation fields are intentionally excluded from the API response shape.
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';

export interface JurisdictionAdminResponse {
  id: string;
  code: string;
  name: string;
  type: string;
  isActive: boolean;
  displayOrder: number;
  nmvtisCovered: boolean;
  dmvConnected: boolean;
  /** Shown as "Coming Soon" on the home page DMV map. Display only. */
  dmvComingSoon: boolean;
  /** Whether clients may use this state by default. System admins bypass it. */
  mvrEnabledByDefault: boolean;
  supportsTitle: boolean;
  supportsRegistration: boolean;
  hasPublicPortal: boolean;
  contactMailingAddress: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  contactWebsiteUrl: string | null;
  recordRequestUrl: string | null;
  recordRequestFormUrl: string | null;
  recordRequestInstructions: string | null;
  recordRequestFee: string | null;
  dataRetentionDays: number | null; // null = the global default applies
  dataRetentionMaxDays: number | null;
}

// Mirror of UpdateJurisdictionRequest on the backend — every field optional so
// the page only sends what actually changed.
export interface UpdateJurisdictionRequest {
  displayOrder?: number;
  type?: string;
  isActive?: boolean;
  nmvtisCovered?: boolean;
  dmvConnected?: boolean;
  dmvComingSoon?: boolean;
  mvrEnabledByDefault?: boolean;
  supportsTitle?: boolean;
  supportsRegistration?: boolean;
  hasPublicPortal?: boolean;
  contactMailingAddress?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  contactWebsiteUrl?: string | null;
  recordRequestUrl?: string | null;
  recordRequestFormUrl?: string | null;
  recordRequestInstructions?: string | null;
  recordRequestFee?: string | null;
  dataRetentionDays?: number; // 0 clears it so the global default applies
}

export const listJurisdictions = async (): Promise<JurisdictionAdminResponse[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.JURISDICTIONS.LIST);
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const getJurisdiction = async (code: string): Promise<JurisdictionAdminResponse> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.JURISDICTIONS.GET(code));
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const updateJurisdiction = async (
  code: string,
  request: UpdateJurisdictionRequest,
): Promise<JurisdictionAdminResponse> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.JURISDICTIONS.UPDATE(code), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

// ---- Per-state report "template": section/field visibility modes for this state ----

// Matches the backend ReportVisibility enum (numeric — no string-enum converter).
export enum ReportVisibility { Visible = 0, Hidden = 1, NullVisible = 2 }

export interface ReportSectionVisibility {
  key: string;
  label: string;
  description: string;
  visibility: ReportVisibility;
}

export const getReportSections = async (code: string): Promise<ReportSectionVisibility[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.JURISDICTIONS.REPORT_SECTIONS(code));
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const saveReportSections = async (
  code: string,
  sections: { key: string; visibility: ReportVisibility }[],
): Promise<ReportSectionVisibility[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.JURISDICTIONS.REPORT_SECTIONS(code), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sections }),
  });
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

// ---- Field-level report template: which individual fields show for this state ----

export interface ReportFieldVisibility {
  key: string;
  label: string;
  sectionKey: string;
  visibility: ReportVisibility;
}

export const getReportFields = async (code: string): Promise<ReportFieldVisibility[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.JURISDICTIONS.REPORT_FIELDS(code));
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const saveReportFields = async (
  code: string,
  fields: { key: string; visibility: ReportVisibility }[],
): Promise<ReportFieldVisibility[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.JURISDICTIONS.REPORT_FIELDS(code), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fields }),
  });
  if (!response.ok) throw await createApiError(response);
  return response.json();
};
